import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, User, Users, Edit, Trash2, Mail, Phone, MessageSquare, Upload, Key, Loader2, Briefcase, Calendar, Palmtree, Clock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Switch } from '../../../components/ui/switch';
import { AddressAutocomplete } from '../../../components/AddressAutocomplete';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { companyApi } from '../../../lib/api';
import { CollaboratorsGuideTour } from '../../../components/GuideTour';
import { getAssetUrl as getAvatarUrl } from '../../../lib/assetUrl';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import useFabAction from '../../../hooks/useFabAction';

export default function CollaboratorsPage() {
  const [collaborators, setCollaborators] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollab, setEditingCollab] = useState(null);
  const [uploadingAvatarId, setUploadingAvatarId] = useState(null);
  const [sendingResetId, setSendingResetId] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', email: '', phone: '', password: '', assigned_services: [], role: 'collaborator',
    starting_address: '', starting_coords: null,
  });
  
  // Leave management states
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [selectedCollabForLeave, setSelectedCollabForLeave] = useState(null);
  const [collabLeaves, setCollabLeaves] = useState([]);
  const [isLoadingLeaves, setIsLoadingLeaves] = useState(false);
  const [newLeave, setNewLeave] = useState({
    start_date: '', end_date: '', leave_type: 'vacation', reason: ''
  });
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);

  // Custom working hours states
  const [isHoursDialogOpen, setIsHoursDialogOpen] = useState(false);
  const [selectedCollabForHours, setSelectedCollabForHours] = useState(null);
  const [hoursEnabled, setHoursEnabled] = useState(false);
  const [hoursGrid, setHoursGrid] = useState([]); // 7 entries indexed by day_of_week
  const [companyHoursGrid, setCompanyHoursGrid] = useState([]);
  const [isLoadingHours, setIsLoadingHours] = useState(false);
  const [isSavingHours, setIsSavingHours] = useState(false);
  const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  
  const LEAVE_TYPES = [
    { value: 'vacation', label: 'Congés payés', color: 'bg-green-100 text-green-700' },
    { value: 'sick', label: 'Maladie', color: 'bg-red-100 text-red-700' },
    { value: 'training', label: 'Formation', color: 'bg-blue-100 text-blue-700' },
    { value: 'personal', label: 'Personnel', color: 'bg-purple-100 text-purple-700' },
    { value: 'other', label: 'Autre', color: 'bg-gray-100 text-gray-700' },
  ];

  const handleAvatarUpload = async (collabId, file) => {
    if (!file) return;
    setUploadingAvatarId(collabId);
    try {
      await companyApi.uploadCollaboratorAvatar(collabId, file);
      toast.success('Avatar mis à jour');
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingAvatarId(null);
    }
  };

  const fetchData = async () => {
    try {
      const [collabRes, servicesRes] = await Promise.all([
        companyApi.getCollaborators(),
        companyApi.getServices()
      ]);
      setCollaborators(collabRes.data.filter(c => c.is_active));
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Leave management functions
  const openLeaveDialog = async (collab) => {
    setSelectedCollabForLeave(collab);
    setIsLeaveDialogOpen(true);
    setIsLoadingLeaves(true);
    setNewLeave({ start_date: '', end_date: '', leave_type: 'vacation', reason: '' });
    try {
      const res = await companyApi.getCollaboratorLeaves(collab.id);
      setCollabLeaves(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des congés');
    } finally {
      setIsLoadingLeaves(false);
    }
  };

  const handleCreateLeave = async () => {
    if (!newLeave.start_date || !newLeave.end_date) {
      toast.error('Veuillez sélectionner les dates de début et de fin');
      return;
    }
    setIsSubmittingLeave(true);
    try {
      await companyApi.createCollaboratorLeave(selectedCollabForLeave.id, newLeave);
      toast.success('Congés enregistrés');
      // Refresh leaves
      const res = await companyApi.getCollaboratorLeaves(selectedCollabForLeave.id);
      setCollabLeaves(res.data || []);
      setNewLeave({ start_date: '', end_date: '', leave_type: 'vacation', reason: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Supprimer cette période de congés ?')) return;
    try {
      await companyApi.deleteLeave(leaveId);
      setCollabLeaves(prev => prev.filter(l => l.id !== leaveId));
      toast.success('Congés supprimés');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getLeaveTypeConfig = (type) => LEAVE_TYPES.find(t => t.value === type) || LEAVE_TYPES[4];

  // ---- Custom working hours ----
  const buildEmptyGrid = () => Array.from({ length: 7 }, (_, i) => ({
    day_of_week: i,
    start_time: '08:00',
    end_time: '18:00',
    is_active: false,
    morning: { start: '08:00', end: '12:00', active: false },
    afternoon: { start: '14:00', end: '18:00', active: false },
    full_day: { start: '08:00', end: '18:00', active: false },
  }));

  const openHoursDialog = async (collab) => {
    setSelectedCollabForHours(collab);
    setIsHoursDialogOpen(true);
    setIsLoadingHours(true);
    try {
      const res = await companyApi.getCollaboratorWorkHours(collab.id);
      const enabled = !!res.data?.custom_hours_enabled;
      setHoursEnabled(enabled);
      const company = res.data?.company_work_hours || buildEmptyGrid();
      setCompanyHoursGrid(company);
      // Pre-fill with existing custom hours, or the company grid as baseline
      const source = res.data?.custom_work_hours || company;
      // Normalise to 7 rows
      const grid = buildEmptyGrid();
      source.forEach(row => {
        if (typeof row?.day_of_week === 'number' && row.day_of_week >= 0 && row.day_of_week < 7) {
          grid[row.day_of_week] = {
            ...grid[row.day_of_week],
            ...row,
            morning: { ...grid[row.day_of_week].morning, ...(row.morning || {}) },
            afternoon: { ...grid[row.day_of_week].afternoon, ...(row.afternoon || {}) },
            full_day: { ...grid[row.day_of_week].full_day, ...(row.full_day || {}) },
          };
        }
      });
      setHoursGrid(grid);
    } catch (error) {
      toast.error('Erreur lors du chargement des horaires');
      setHoursGrid(buildEmptyGrid());
    } finally {
      setIsLoadingHours(false);
    }
  };

  const togglePeriod = (dayIdx, period) => {
    setHoursGrid(prev => prev.map((row, i) => {
      if (i !== dayIdx) return row;
      const nextActive = !row[period]?.active;
      const next = { ...row, [period]: { ...row[period], active: nextActive } };
      // Toggling full_day off if morning/afternoon are turned on (and vice-versa)
      if (period === 'full_day' && nextActive) {
        next.morning = { ...next.morning, active: false };
        next.afternoon = { ...next.afternoon, active: false };
      } else if ((period === 'morning' || period === 'afternoon') && nextActive) {
        next.full_day = { ...next.full_day, active: false };
      }
      return next;
    }));
  };

  const resetGridFromCompany = () => {
    setHoursGrid(companyHoursGrid.length ? companyHoursGrid.map(row => ({
      ...row,
      morning: { ...row.morning },
      afternoon: { ...row.afternoon },
      full_day: { ...row.full_day },
    })) : buildEmptyGrid());
    toast.success('Grille pré-remplie avec les horaires de l\'entreprise');
  };

  const saveCustomHours = async () => {
    if (!selectedCollabForHours) return;
    setIsSavingHours(true);
    try {
      await companyApi.updateCollaboratorWorkHours(selectedCollabForHours.id, {
        custom_hours_enabled: hoursEnabled,
        custom_work_hours: hoursGrid,
      });
      toast.success('Horaires personnalisés enregistrés');
      setIsHoursDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSavingHours(false);
    }
  };

  const formatLeaveDate = (start, end) => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    if (start === end) {
      return format(startDate, 'd MMMM yyyy', { locale: fr });
    }
    return `${format(startDate, 'd MMM', { locale: fr })} - ${format(endDate, 'd MMM yyyy', { locale: fr })}`;
  };

  const hasUpcomingLeave = (collabId) => {
    const now = new Date();
    return collabLeaves.some(l => 
      l.collaborator_id === collabId && isAfter(parseISO(l.end_date), now)
    );
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', password: '', assigned_services: [], role: 'collaborator', starting_address: '', starting_coords: null });
    setEditingCollab(null);
  };

  const handleOpenDialog = (collab = null) => {
    if (collab) {
      setEditingCollab(collab);
      setFormData({
        name: collab.name, email: collab.email || '', phone: collab.phone || '',
        password: '', assigned_services: collab.assigned_services || [], role: collab.role || 'collaborator',
        starting_address: collab.starting_address || '', starting_coords: collab.starting_coords || null,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  useFabAction({ new: () => { resetForm(); setEditingCollab(null); setIsDialogOpen(true); } });

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      assigned_services: prev.assigned_services.includes(serviceId)
        ? prev.assigned_services.filter(id => id !== serviceId)
        : [...prev.assigned_services, serviceId]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Le nom est obligatoire');
      return;
    }
    try {
      if (editingCollab) {
        const updateData = {
          name: formData.name, email: formData.email || null,
          phone: formData.phone || null, assigned_services: formData.assigned_services,
          role: formData.role,
          starting_address: formData.starting_address || null,
          starting_coords: formData.starting_coords || null,
        };
        if (formData.password) updateData.password = formData.password;
        await companyApi.updateCollaborator(editingCollab.id, updateData);
        toast.success('Collaborateur mis à jour');
      } else {
        // Strip empty-string values so Optional/EmailStr fields don't reject
        // the payload with a 422 (empty email is not a valid EmailStr).
        const payload = {
          ...formData,
          email: formData.email?.trim() || null,
          phone: formData.phone?.trim() || null,
          password: formData.password?.trim() || null,
        };
        await companyApi.createCollaborator(payload);
        toast.success('Collaborateur ajouté');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce collaborateur ?')) return;
    try {
      await companyApi.deleteCollaborator(id);
      toast.success('Collaborateur supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendPasswordReset = async (collab) => {
    if (!collab.email) {
      toast.error('Ce collaborateur n\'a pas d\'adresse email');
      return;
    }
    setSendingResetId(collab.id);
    try {
      await companyApi.sendCollaboratorPasswordReset(collab.id);
      toast.success(`Lien de réinitialisation envoyé à ${collab.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingResetId(null);
    }
  };

  const getServiceNames = (serviceIds) => {
    if (!serviceIds || serviceIds.length === 0) return [];
    // Filtrer le marqueur spécial '__none__'
    const filteredIds = serviceIds.filter(id => id !== '__none__');
    return filteredIds.map(id => services.find(s => s.id === id)?.name).filter(Boolean);
  };

  return (
    <div className="space-y-6">
      <CollaboratorsGuideTour />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Collaborateurs</h1>
          <p className="text-muted-foreground mt-1">Gérez votre équipe et leurs services assignés</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-collaborator-btn" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingCollab ? 'Modifier le collaborateur' : 'Nouveau Collaborateur'}
              </DialogTitle>
              <DialogDescription>
                {editingCollab ? 'Modifiez les informations et services assignés' : 'Ajoutez un collaborateur avec accès à son espace dédié'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input data-testid="collab-name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jean Dupont" />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input data-testid="collab-phone" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="06 12 34 56 78" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email (pour connexion)</Label>
                <Input data-testid="collab-email" type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jean@email.fr" />
              </div>
              <div className="space-y-2">
                <Label>{editingCollab ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}</Label>
                <Input data-testid="collab-password" type="password" value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" />
              </div>
              
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="collab-role">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="collaborator">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>Collaborateur</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="commercial">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>Commercial</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.role === 'commercial' 
                    ? 'Le commercial gère les demandes de devis et son agenda de RDV'
                    : 'Le collaborateur intervient sur les prestations de services'}
                </p>
              </div>

              {/* Home / depot address for travel-time calc */}
              <div className="space-y-2">
                <Label>Adresse de départ (RH)</Label>
                <AddressAutocomplete
                  value={formData.starting_address}
                  onChange={(v) => setFormData({ ...formData, starting_address: v })}
                  onSelect={(sel) => setFormData(prev => ({
                    ...prev,
                    starting_address: sel.label,
                    starting_coords: sel.coords || null,
                  }))}
                  placeholder="Domicile ou dépôt du collaborateur"
                  data-testid="collab-starting-address"
                />
                <p className="text-xs text-muted-foreground">
                  Optionnel — utilisé pour calculer le temps de trajet réel vers les clients. Sans adresse, l'origine de l'entreprise est utilisée par défaut.
                </p>
              </div>
              
              {/* Services assignés - pour collaborateurs ET commerciaux */}
              <div className="space-y-2">
                <Label>Services assignés</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  {formData.role === 'commercial' 
                    ? 'Ce commercial recevra les demandes liées à ces services uniquement'
                    : 'Ce collaborateur sera proposé uniquement pour ces services'}
                </p>
                
                {/* Option pour aucun service (Commercial RDV uniquement) */}
                <div 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors mb-2 border-2 ${
                    formData.assigned_services.includes('__none__') ? 'bg-orange-50 border-orange-300' : 'border-dashed border-muted hover:border-muted-foreground'
                  }`}
                  onClick={() => setFormData({ ...formData, assigned_services: ['__none__'] })}
                >
                  <Checkbox checked={formData.assigned_services.includes('__none__')}
                    onCheckedChange={() => setFormData({ ...formData, assigned_services: ['__none__'] })} />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Aucun service (RDV uniquement)</p>
                    <p className="text-xs text-muted-foreground">
                      Ce collaborateur ne fait que des rendez-vous commerciaux, pas d'interventions
                    </p>
                  </div>
                </div>
                
                {/* Option pour tous les services */}
                <div 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors mb-2 border-2 ${
                    formData.assigned_services.length === 0 ? 'bg-green-50 border-green-300' : 'border-dashed border-muted hover:border-muted-foreground'
                  }`}
                  onClick={() => setFormData({ ...formData, assigned_services: [] })}
                >
                  <Checkbox checked={formData.assigned_services.length === 0}
                    onCheckedChange={() => setFormData({ ...formData, assigned_services: [] })} />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Disponible pour tous les services</p>
                    <p className="text-xs text-muted-foreground">
                      {formData.role === 'commercial' 
                        ? 'Ce commercial recevra toutes les demandes de devis'
                        : 'Ce collaborateur pourra être assigné à n\'importe quel service'}
                    </p>
                  </div>
                </div>
                
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {services.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun service disponible</p>
                  ) : (
                    services.map((service) => (
                      <div key={service.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          formData.assigned_services.includes(service.id) ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted'
                        }`}
                        onClick={() => toggleService(service.id)}>
                        <Checkbox checked={formData.assigned_services.includes(service.id)}
                          onCheckedChange={() => toggleService(service.id)} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{service.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {service.price_type === 'fixed' ? `${service.fixed_price}€` : `${service.price_per_hour}€/h`}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <Button className="w-full rounded-full" data-testid="submit-collab-btn" onClick={handleSubmit}>
                {editingCollab ? 'Enregistrer' : 'Ajouter le collaborateur'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Espace Collaborateur</p>
            <p className="text-sm text-blue-700">Vos collaborateurs peuvent se connecter via l'onglet "Équipe" sur la page de connexion.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">Chargement...</div>
          ) : collaborators.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun collaborateur</p>
              <p className="text-sm mt-1">Ajoutez votre premier collaborateur pour commencer</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Services assignés</TableHead>
                  <TableHead>Accès</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collaborators.map((collab) => (
                  <TableRow key={collab.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="relative group">
                          {collab.avatar_url ? (
                            <img src={getAvatarUrl(collab.avatar_url)} alt={collab.name}
                              className="w-10 h-10 rounded-full object-cover border" loading="lazy" decoding="async" />
                          ) : (
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                          )}
                          <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            {uploadingAvatarId === collab.id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 text-white" />
                            )}
                            <input type="file" accept="image/*" className="hidden"
                              onChange={(e) => handleAvatarUpload(collab.id, e.target.files[0])}
                              disabled={uploadingAvatarId === collab.id} />
                          </label>
                        </div>
                        <div>
                          <p className="font-medium">{collab.name}</p>
                          {collab.role === 'commercial' && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs mt-1">Commercial</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {collab.email && <p className="text-sm flex items-center gap-1"><Mail className="w-3 h-3 text-muted-foreground" />{collab.email}</p>}
                      {collab.phone && <p className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{collab.phone}</p>}
                      {!collab.email && !collab.phone && <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {collab.assigned_services?.includes('__none__') ? (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">RDV uniquement</Badge>
                        ) : getServiceNames(collab.assigned_services).length > 0 ? (
                          getServiceNames(collab.assigned_services).map((name, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{name}</Badge>
                          ))
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">Tous les services</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {collab.email ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Connexion activée</Badge>
                      ) : (
                        <Badge variant="secondary">Sans accès</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title="Horaires personnalisés"
                          data-testid={`manage-hours-${collab.id}`}
                          onClick={() => openHoursDialog(collab)}
                        >
                          <Clock className={`w-4 h-4 ${collab.custom_hours_enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title="Gérer les congés"
                          data-testid={`manage-leaves-${collab.id}`}
                          onClick={() => openLeaveDialog(collab)}
                        >
                          <Palmtree className="w-4 h-4 text-green-600" />
                        </Button>
                        {collab.email && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            title="Envoyer lien réinitialisation mot de passe"
                            data-testid={`reset-password-${collab.id}`}
                            onClick={() => handleSendPasswordReset(collab)}
                            disabled={sendingResetId === collab.id}
                          >
                            {sendingResetId === collab.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Key className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" data-testid={`edit-collab-${collab.id}`}
                          onClick={() => handleOpenDialog(collab)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                          data-testid={`delete-collab-${collab.id}`} onClick={() => handleDelete(collab.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Leave Management Dialog */}
      <Dialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
            <Palmtree className="w-5 h-5 text-green-600" />
            Congés & Absences de {selectedCollabForLeave?.name}
          </DialogTitle>
          <DialogDescription>
            Gérez les périodes d'absence de ce collaborateur
          </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add new leave form */}
            <div className="p-4 border rounded-lg space-y-3 bg-muted/30">
              <p className="font-medium text-sm">Ajouter une période</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Date de début</Label>
                  <Input
                    type="date"
                    value={newLeave.start_date}
                    onChange={(e) => setNewLeave({...newLeave, start_date: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Date de fin</Label>
                  <Input
                    type="date"
                    value={newLeave.end_date}
                    onChange={(e) => setNewLeave({...newLeave, end_date: e.target.value})}
                    min={newLeave.start_date}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select 
                    value={newLeave.leave_type} 
                    onValueChange={(v) => setNewLeave({...newLeave, leave_type: v})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAVE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Motif (optionnel)</Label>
                  <Input
                    placeholder="Ex: Vacances été"
                    value={newLeave.reason}
                    onChange={(e) => setNewLeave({...newLeave, reason: e.target.value})}
                  />
                </div>
              </div>
              <Button 
                size="sm" 
                className="w-full rounded-full" 
                onClick={handleCreateLeave}
                disabled={isSubmittingLeave || !newLeave.start_date || !newLeave.end_date}
              >
                {isSubmittingLeave ? 'Enregistrement...' : 'Ajouter les congés'}
              </Button>
            </div>

            {/* Leaves list */}
            <div className="space-y-2">
              <p className="font-medium text-sm">Congés enregistrés</p>
              {isLoadingLeaves ? (
                <div className="p-4 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : collabLeaves.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm border rounded-lg border-dashed">
                  Aucun congé enregistré
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {collabLeaves.map(leave => {
                    const typeConfig = getLeaveTypeConfig(leave.leave_type);
                    const isPast = isBefore(parseISO(leave.end_date), new Date());
                    return (
                      <div 
                        key={leave.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${isPast ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={`${typeConfig.color} text-xs`}>
                            {typeConfig.label}
                          </Badge>
                          <div>
                            <p className="text-sm font-medium">
                              {formatLeaveDate(leave.start_date, leave.end_date)}
                            </p>
                            {leave.reason && (
                              <p className="text-xs text-muted-foreground">{leave.reason}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDeleteLeave(leave.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLeaveDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Working Hours Dialog */}
      <Dialog open={isHoursDialogOpen} onOpenChange={setIsHoursDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Horaires personnalisés — {selectedCollabForHours?.name}
            </DialogTitle>
            <DialogDescription>
              Définissez les jours et créneaux (matin / après-midi / journée) où ce collaborateur travaille.
              Les jours désactivés le rendent indisponible dans les widgets publics et le planning.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30" data-testid="custom-hours-toggle-row">
              <div className="flex-1">
                <p className="font-medium text-sm">Activer les horaires personnalisés</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Quand désactivé, ce collaborateur suit les horaires globaux de l'entreprise.
                </p>
              </div>
              <Switch
                data-testid="custom-hours-toggle"
                checked={hoursEnabled}
                onCheckedChange={setHoursEnabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Grille hebdomadaire</p>
              <Button
                variant="ghost"
                size="sm"
                data-testid="reset-hours-from-company"
                onClick={resetGridFromCompany}
                disabled={!hoursEnabled}
              >
                Copier les horaires entreprise
              </Button>
            </div>

            {isLoadingHours ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : (
              <div className={`border rounded-lg overflow-hidden ${!hoursEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="grid grid-cols-[110px_1fr_1fr_1fr] bg-muted/50 text-xs font-medium text-muted-foreground p-2">
                  <div>Jour</div>
                  <div className="text-center">Matin</div>
                  <div className="text-center">Après-midi</div>
                  <div className="text-center">Journée</div>
                </div>
                {hoursGrid.map((row, idx) => {
                  const anyActive = row.morning?.active || row.afternoon?.active || row.full_day?.active;
                  return (
                    <div
                      key={idx}
                      data-testid={`hours-row-${idx}`}
                      className={`grid grid-cols-[110px_1fr_1fr_1fr] items-center p-3 border-t ${anyActive ? 'bg-primary/5' : ''}`}
                    >
                      <div className="text-sm font-medium">{DAY_LABELS[idx]}</div>
                      <div className="flex items-center justify-center">
                        <Checkbox
                          data-testid={`hours-${idx}-morning`}
                          checked={!!row.morning?.active}
                          onCheckedChange={() => togglePeriod(idx, 'morning')}
                        />
                      </div>
                      <div className="flex items-center justify-center">
                        <Checkbox
                          data-testid={`hours-${idx}-afternoon`}
                          checked={!!row.afternoon?.active}
                          onCheckedChange={() => togglePeriod(idx, 'afternoon')}
                        />
                      </div>
                      <div className="flex items-center justify-center">
                        <Checkbox
                          data-testid={`hours-${idx}-full_day`}
                          checked={!!row.full_day?.active}
                          onCheckedChange={() => togglePeriod(idx, 'full_day')}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsHoursDialogOpen(false)} disabled={isSavingHours}>
              Annuler
            </Button>
            <Button
              className="rounded-full"
              data-testid="save-custom-hours"
              onClick={saveCustomHours}
              disabled={isSavingHours || isLoadingHours}
            >
              {isSavingHours ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
